import React, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import { sizes } from '@/common/styles/sizes';
import { 
  Download, 
  FileText, 
  FileImage, 
  FileSpreadsheet, 
  File as FileIcon,
  XCircle,
  Loader,
  CheckCircle2,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { formatFileSize } from '@/common/utils/format';
import { IconButton } from '../iconButton/IconButton';
import { FileProps } from '@/common/types/common';

type FileUploadType = 'sm' | 'md' | 'lg';
type FileUploadStatus = 'idle' | 'uploading' | 'error' | 'success' | 'disabled';

interface FileUploadProps {
  /**
   * 컴포넌트 크기
   * @default 'md'
   */
  type?: FileUploadType;
  
  /**
   * 컴포넌트 너비
   */
  width?: string | number;
  
  /**
   * 컴포넌트 상태
   * @default 'idle'
   */
  status?: FileUploadStatus;
  
  /**
   * 현재 업로드된 파일 목록
   * @default []
   */
  files?: File[];
  
  /**
   * 서버에 저장된 파일 정보
   */
  fileProps?: FileProps[];
  
  /**
   * 다중 파일 업로드 여부
   * @default false
   */
  multiple?: boolean;
  
  /**
   * 허용 파일 확장자 (예: '.jpg,.png,.pdf')
   */
  accept?: string;
  
  /**
   * 파일 최대 크기 (바이트 단위)
   * @default 10485760 (10MB)
   */
  maxSize?: number;
  
  /**
   * 파일 변경 시 호출되는 콜백 함수
   */
  onChange?: (files: File[]) => void;
  
  /**
   * 파일 업로드 영역 커스텀 라벨
   * @default '여기에 파일을 끌어다 놓거나 클릭하세요'
   */
  label?: string;
  
  /**
   * 에러 발생 시 호출되는 콜백 함수
   */
  onError?: (error: string) => void;
  
  /**
   * 에러 메시지
   */
  error?: string;
  
  /**
   * 에러 메시지 표시 여부
   * @default true
   */
  showErrorMessage?: boolean;
  
  /**
   * 파일 목록 표시 최대 개수 (0: 무제한)
   * @default 0
   */
  maxFileCount?: number;

  /**
   * 컴포넌트 비활성화 여부
   * @default false
   */
  disabled?: boolean;
  
  /**
   * 파일 재시도 시 호출되는 콜백 함수
   */
  onRetry?: (file: File) => void;
  
  /**
   * FileProps 삭제 시 호출되는 콜백 함수
   */
  onFilePropsRemove?: (fileProps: FileProps) => void;
}

/**
 * 파일 업로드 컴포넌트
 */
const FileUpload: React.FC<FileUploadProps> = ({
  type = 'md',
  width,
  status = 'idle',
  files = [],
  fileProps = [],
  multiple = false,
  accept,
  disabled = false,
  maxSize = 50 * 1024 * 1024, // 50MB
  onChange,
  label = '여기에 파일을 끌어다 놓거나 클릭하세요',
  onError,
  error,
  showErrorMessage = true,
  maxFileCount = 0,
  onRetry,
  onFilePropsRemove,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // disabled 상태이거나 status가 'disabled'일 때 비활성화
  const isDisabled = disabled || status === 'disabled';
  
  /**
   * 드래그 이벤트 처리
   */
  const handleDragEvents = useCallback((e: React.DragEvent<HTMLDivElement>, isDraggingState: boolean) => {
    e.preventDefault();
    e.stopPropagation();
    
    // disabled 상태일 때는 드래그 이벤트 무시
    if (isDisabled) return;
    
    setIsDragging(isDraggingState);
  }, [isDisabled]);
  
  /**
   * 파일 유효성 검사
   */
  const validateFile = useCallback((file: File): string | null => {
    // 파일 크기 검사
    if (file.size > maxSize) {
      return `파일 크기가 너무 큽니다. 최대 ${formatFileSize(maxSize)}까지 가능합니다.`;
    }
    
    // 파일 타입 검사
    if (accept) {
      const acceptedTypes = accept.split(',').map(type => type.trim().toLowerCase());
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      
      if (!fileExtension) {
        return '파일 확장자가 없습니다.';
      }
      
      const isAccepted = acceptedTypes.some(type => {
        // MIME 타입 검사 (e.g. 'image/*')
        if (type.includes('/*')) {
          const mimePrefix = type.split('/')[0];
          return file.type.startsWith(mimePrefix);
        }
        
        // 확장자 검사 (e.g. '.jpg')
        if (type.startsWith('.')) {
          return `.${fileExtension}` === type;
        }
        
        // 전체 MIME 타입 검사 (e.g. 'image/jpeg')
        return file.type === type;
      });
      
      if (!isAccepted) {
        return `지원하지 않는 파일 형식입니다. ${accept} 형식만 가능합니다.`;
      }
    }
    
    return null;
  }, [accept, maxSize]);
  
  /**
   * 파일 처리
   */
  const processFiles = useCallback((fileList: FileList) => {
    // disabled 상태일 때는 파일 처리하지 않음
    if (isDisabled) return;
    
    // 다중 업로드가 아닌 경우 하나의 파일만 선택
    const newFiles: File[] = [];
    const errors: string[] = [];
    
    Array.from(fileList).forEach(file => {
      const validationError = validateFile(file);
      
      if (validationError) {
        errors.push(`${file.name}: ${validationError}`);
      } else {
        newFiles.push(file);
      }
    });
    
    // 에러 처리
    if (errors.length > 0) {
      const errorMessage = errors.join('\n');
      onError?.(errorMessage);
    }
    
    // 파일 업데이트
    const updatedFiles = multiple
      ? [...files, ...newFiles]
      : newFiles.length > 0 ? [newFiles[0]] : [];
    
    // 최대 파일 개수 제한 적용
    const finalFiles = maxFileCount > 0 
      ? updatedFiles.slice(0, maxFileCount) 
      : updatedFiles;
    
    onChange?.(finalFiles);
  }, [files, multiple, onChange, onError, validateFile, maxFileCount, isDisabled]);
  
  /**
   * 파일 드롭 처리
   */
  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    // disabled 상태일 때는 드롭 처리하지 않음
    if (isDisabled) return;
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  }, [processFiles, isDisabled]);
  
  /**
   * 파일 선택 처리
   */
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    // disabled 상태일 때는 파일 변경 처리하지 않음
    if (isDisabled) return;
    
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
    }
  }, [processFiles, isDisabled]);
  
  /**
   * 파일 삭제 처리
   */
  const handleRemoveFile = useCallback((index: number, isFileProps?: boolean) => {
    // disabled 상태일 때는 파일 삭제하지 않음
    if (isDisabled) return;
    
    if (isFileProps) {
      const fileToRemove = fileProps[index];
      onFilePropsRemove?.(fileToRemove);
    } else {
      const updatedFiles = [...files];
      updatedFiles.splice(index, 1);
      onChange?.(updatedFiles);
    }
    // input value 초기화
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [files, fileProps, onChange, onFilePropsRemove, isDisabled]);
  
  /**
   * 파일 업로드 영역 클릭 처리
   */
  const handleClick = useCallback(() => {
    // disabled 상태일 때는 클릭 처리하지 않음
    if (isDisabled) return;
    
    fileInputRef.current?.click();
  }, [isDisabled]);

  const getStatusIcon = () => {
    switch (status) {
      case 'uploading':
        return <Loader size={24} color={semanticColors.icon.brand} className="animate-spin" />;
      case 'success':
        return files.length > 0 ? getFileIcon(files[0].name) : <CheckCircle2 size={24} color={semanticColors.icon.onSuccess} />;
      case 'error':
        return <AlertCircle size={24} color={semanticColors.icon.danger} />;
      case 'disabled':
        return <FileIcon size={24} color={semanticColors.icon.disabled} />;
      default:
        return <FileIcon size={24} color={semanticColors.icon.tertiary} />;
    }
  };

  const getStatusColor = (status: FileUploadStatus) => {
    switch (status) {
      case 'uploading':
        return semanticColors.text.brand;
      case 'error':
        return semanticColors.text.onDanger.secondary;
      case 'success':
        return semanticColors.text.onSuccess.primary;
      case 'disabled':
        return semanticColors.text.disabled;
      default:
        return semanticColors.text.primary;
    }
  };

  const handleRetry = useCallback((file: File) => {
    // disabled 상태일 때는 재시도하지 않음
    if (isDisabled) return;
    
    onRetry?.(file);
  }, [onRetry, isDisabled]);

  const renderFileInfo = (file: File | FileProps) => {
    const isFileProps = 'url' in file;
    const fileName = isFileProps ? file.name : (file as File).name;
    const fileSize = isFileProps ? file.size : (file as File).size;

    return (
      <FileNameWrapper>
        <Download size={16} style={{minWidth: '16px', minHeight: '16px'}} color={isDisabled ? semanticColors.icon.disabled : semanticColors.text.primary} />
        <FileName $status={status} title={fileName} $disabled={isDisabled}>{fileName}</FileName>
        <FileSize $status={status} $disabled={isDisabled}>{formatFileSize(fileSize)}</FileSize>
      </FileNameWrapper>
    );
  };

  return (
    <Container $type={type} $width={width}>
      <UploadArea
        $type={type}
        $status={status}
        $isDragging={isDragging}
        $disabled={isDisabled}
        onClick={handleClick}
        onDragEnter={(e) => handleDragEvents(e, true)}
        onDragOver={(e) => handleDragEvents(e, true)}
        onDragLeave={(e) => handleDragEvents(e, false)}
        onDrop={handleDrop}
        disabled={isDisabled}
      >
        {type === 'sm' ? (
          <>
            <FileInfo>
              {files.length > 0 ? (
                renderFileInfo(files[0])
              ) : fileProps.length > 0 ? (
                renderFileInfo(fileProps[0])
              ) : (
                <FileNameWrapper>
                  <Download size={16} style={{minWidth: '16px', minHeight: '16px'}} color={isDisabled ? semanticColors.icon.disabled : semanticColors.text.primary} />
                  <UploadText $disabled={isDisabled}>{label}</UploadText>
                </FileNameWrapper>
              )}
            </FileInfo>
            {status === 'error' && files.length > 0 && !isDisabled && (
              <RefreshCw 
                size={16} 
                color={semanticColors.icon.danger} 
                onClick={() => handleRetry(files[0])}
                style={{ cursor: 'pointer' }}
              />
            )}
            {(files.length > 0 || fileProps.length > 0) && !isDisabled && (
              <DeleteButton onClick={(e) => {
                e.stopPropagation();
                handleRemoveFile(0, fileProps.length > 0 && files.length === 0);
              }}>
                <XCircle size={20} color={semanticColors.icon.tertiary} />
              </DeleteButton>
            )}
          </>
        ) : type === 'md' ? (
          <CardLayout>
            {status === 'success' || (files.length > 0 || fileProps.length > 0) ? (
              <>
                {getFileIcon(files.length > 0 ? files[0].name : fileProps[0].name)}
                <FileInfoColumn>
                  <TruncatedFileName $disabled={isDisabled}>
                    {files.length > 0 ? files[0].name : fileProps[0].name}
                  </TruncatedFileName>
                  <FileSize $disabled={isDisabled}>
                    {formatFileSize(files.length > 0 ? files[0].size : fileProps[0].size)}
                  </FileSize>
                </FileInfoColumn>
                {!isDisabled && (
                  <DeleteTextButton onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile(0, fileProps.length > 0 && files.length === 0);
                  }}>
                    삭제
                  </DeleteTextButton>
                )}
              </>
            ) : (
              <>
                {getStatusIcon()}
                <UploadText $disabled={isDisabled}>{label}</UploadText>
                {(accept || maxSize) && (
                  <FileInfoText $disabled={isDisabled}>
                    {accept && `${accept.split(',').map(ext => ext.replace('.', '').toUpperCase()).join(', ')}`}
                    {accept && maxSize && ' · '}
                    {maxSize && `최대 ${formatFileSize(maxSize)}`}
                  </FileInfoText>
                )}
                {status === 'error' && files.length > 0 && !isDisabled && (
                  <RetryButton onClick={() => handleRetry(files[0])}>
                    다시 시도
                  </RetryButton>
                )}
              </>
            )}
          </CardLayout>
        ) : (
          <>
            {getStatusIcon()}
            <UploadText $disabled={isDisabled}>{label}</UploadText>
            {(accept || maxSize) && (
              <FileInfoText $disabled={isDisabled}>
                {accept && `${accept.split(',').map(ext => ext.replace('.', '').toUpperCase()).join(', ')}`}
                {accept && maxSize && ' · '}
                {maxSize && `최대 ${formatFileSize(maxSize)}`}
              </FileInfoText>
            )}
          </>
        )}
        <HiddenInput
          type="file"
          ref={fileInputRef}
          multiple={multiple}
          accept={accept}
          onChange={handleFileChange}
          disabled={isDisabled}
        />
      </UploadArea>
      
      {/* 파일 목록 */}
      {type === 'lg' && (files.length > 0 || fileProps.length > 0) && (
        <FileListContainer>
          {/* 현재 선택된 파일 목록 */}
          {files.map((file, index) => (
            <FileItemRow key={`file-${index}`}>
              {renderFileInfo(file)}
              <ActionButtons>
                {status === 'error' && !isDisabled && (
                  <RefreshCw 
                    size={16} 
                    color={semanticColors.icon.danger} 
                    onClick={() => handleRetry(file)}
                    style={{ cursor: 'pointer' }}
                  />
                )}
                {!isDisabled && (
                  <DeleteButton onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile(index);
                  }}>
                    <XCircle size={20} color={semanticColors.icon.tertiary} />
                  </DeleteButton>
                )}
              </ActionButtons>
            </FileItemRow>
          ))}
          {/* 서버에 저장된 파일 목록 */}
          {fileProps.map((file, index) => (
            <FileItemRow key={`fileProps-${index}`}>
              {renderFileInfo(file)}
              <ActionButtons>
                {!isDisabled && (
                  <DeleteButton onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveFile(index, true);
                  }}>
                    <XCircle size={20} color={semanticColors.icon.tertiary} />
                  </DeleteButton>
                )}
              </ActionButtons>
            </FileItemRow>
          ))}
        </FileListContainer>
      )}
    </Container>
  );
};

const getStatusStyles = (status: FileUploadStatus, isDragging: boolean, disabled: boolean) => {
  if (disabled || status === 'disabled') {
    return `
      background-color: ${semanticColors.background.disabled};
      border-color: ${semanticColors.border.disabled};
      cursor: not-allowed;
      opacity: 0.6;
    `;
  }
  
  switch (status) {
    case 'uploading':
      return `
        background-color: ${semanticColors.background.secondary};
        border-color: ${semanticColors.border.brand};
      `;
    case 'error':
      return `
        background-color: ${semanticColors.background.danger.secondary};
        border-color: ${semanticColors.border.dangerPrimary};
      `;
    case 'success':
      return `
        background-color: ${semanticColors.background.secondary};
        border-color: ${semanticColors.border.tertiary};
      `;
    default:
      return `
        background-color: ${isDragging 
          ? semanticColors.background.tertiary 
          : semanticColors.background.secondary};
        border-color: ${isDragging 
          ? semanticColors.border.brand 
          : semanticColors.border.primary};
      `;
  }
};

const getSizeStyles = (type: FileUploadType) => {
  switch (type) {
    case 'sm':
      return `
        height: 1.625rem;
        flex-direction: row;
        justify-content: space-between;
        padding: ${sizes.padding[8]} ${sizes.padding[12]};
        ${typography.button.button5}
      `;
    case 'md':
      return `
        height: 7.3125rem;
        flex-direction: column;
        justify-content: center;
        padding: ${sizes.padding[16]};
        line-height: 150%;
      `;
    case 'lg':
      return `
        height: 200px;
        flex-direction: column;
        justify-content: center;
        padding: ${sizes.padding[24]};
      `;
  }
};

// 스타일 컴포넌트
const Container = styled.div<{ $type: FileUploadType; $width?: string | number }>`
  width: ${props => props.$width ? (typeof props.$width === 'number' ? `${props.$width}px` : props.$width) : '100%'};
  display: flex;
  flex-direction: column;
  gap: ${props => props.$type === 'lg' ? sizes.gap[12] : sizes.gap[8]};
`;

const UploadArea = styled.div<{
  $type: FileUploadType;
  $status: FileUploadStatus;
  $isDragging: boolean;
  $disabled: boolean;
  disabled?: boolean;
}>`
  height: fit-content;
  display: flex;
  align-items: center;
  border: 1px solid ${props => props.$isDragging && !props.$disabled ? semanticColors.border.brand : semanticColors.border.primary};
  border-radius: ${sizes.radius.lg};
  background-color: ${semanticColors.background.secondary};
  transition: all 0.2s ease-in-out;
  gap: ${sizes.gap[8]};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  
  ${props => getSizeStyles(props.$type)}
  ${props => getStatusStyles(props.$status, props.$isDragging, props.$disabled)}
  
  &:hover {
    ${props => !props.$disabled && `
      filter: brightness(0.98);
    `}
  }
`;

const UploadText = styled.p<{ $disabled?: boolean }>`
  ${typography.paragraph.p2.medium};
  color: ${props => props.$disabled ? semanticColors.text.disabled : semanticColors.text.tertiary};
  margin: 0;
  text-align: center;
  line-height: 150%;
`;

const AcceptedFormats = styled.p`
  ${typography.caption.caption2};
  color: ${semanticColors.text.tertiary};
  margin: 0;
  text-align: center;
`;

const HiddenInput = styled.input`
  display: none;
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.gap[8]};
  padding: ${sizes.padding[8]} ${sizes.padding[12]};
  background-color: ${semanticColors.background.danger.secondary};
  border-radius: ${sizes.radius.lg};
  border: 1px solid ${semanticColors.border.dangerSecondary};
`;

const ErrorText = styled.p`
  ${typography.paragraph.p2.default};
  color: ${semanticColors.text.onDanger.secondary};
  margin: 0;
`;

const FileListContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${semanticColors.border.primary};
  border-radius: ${sizes.radius.lg};
  overflow: hidden;
  background-color: ${semanticColors.background.secondary};
`;

const FileItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 1.625rem;
  padding: ${sizes.padding[8]} ${sizes.padding[12]};
`;

const FileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizes.gap[4]};
  
  overflow: hidden;
  flex: 1;
`;

const FileName = styled.span<{ $status?: FileUploadStatus; $disabled?: boolean }>`
  ${typography.paragraph.p2.medium};
  color: ${props => {
    if (props.$disabled) return semanticColors.text.disabled;
    if (!props.$status) return semanticColors.text.primary;
    switch (props.$status) {
      case 'uploading':
        return semanticColors.text.primary;
      case 'error':
        return semanticColors.text.primary;
      case 'success':
        return semanticColors.text.primary;
      case 'disabled':
        return semanticColors.text.disabled;
      default:
        return semanticColors.text.primary;
    }
  }};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileSize = styled.span<{ $status?: FileUploadStatus; $disabled?: boolean }>`
  ${typography.caption.caption1.default};
  color: ${props => {
    if (props.$disabled) return semanticColors.text.disabled;
    if (!props.$status) return semanticColors.text.tertiary;
    switch (props.$status) {
      case 'uploading':
        return semanticColors.text.brand;
      case 'error':
        return semanticColors.text.onDanger.secondary;
      case 'success':
        return semanticColors.text.onSuccess.primary;
      case 'disabled':
        return semanticColors.text.disabled;
      default:
        return semanticColors.text.tertiary;
    }
  }};
  margin-left: ${sizes.margin[8]};
  flex-shrink: 0;
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: ${sizes.padding[4]};
  border-radius: ${sizes.radius.full};
  
  &:hover {
    background-color: ${semanticColors.background.tertiary};
  }
`;

const FileNameWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.gap[8]};
  padding: ${sizes.padding[6]} ${sizes.padding[8]};
  overflow: hidden;
`;

const CardLayout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  gap: ${sizes.gap[8]};
  text-align: center;
`;

const TruncatedFileName = styled.span<{ $disabled?: boolean }>`
  ${typography.paragraph.p2.medium};
  color: ${props => props.$disabled ? semanticColors.text.disabled : semanticColors.text.primary};
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DeleteTextButton = styled.button`
  ${typography.paragraph.p2.medium};
  color: ${semanticColors.text.brand};
  background: none;
  border: none;
  cursor: pointer;
  border-radius: ${sizes.radius.sm};
  
  &:hover {
    background-color: ${semanticColors.background.tertiary};
  }
`;

const FileInfoText = styled.p<{ $disabled?: boolean }>`
  ${typography.caption.caption1.default};
  color: ${props => props.$disabled ? semanticColors.text.disabled : semanticColors.text.tertiary};
  margin: 0;
  line-height: 150%;
`;

const RetryButton = styled.button`
  ${typography.paragraph.p2.medium};
  color: ${semanticColors.text.onDanger.secondary};
  background: none;
  border: none;
  cursor: pointer;
  border-radius: ${sizes.radius.sm};
  
  &:hover {
    text-decoration: underline;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${sizes.gap[4]};
`;

const FileInfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${sizes.gap[4]};
  line-height: 150%;
`;

const getFileIcon = (fileName: string) => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  
  switch (extension) {
    case 'pdf':
      return <FileText size={24} />;
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
      return <FileImage size={24} />;
    case 'xls':
    case 'xlsx':
    case 'csv':
      return <FileSpreadsheet size={24} />;
    case 'doc':
    case 'docx':
    case 'txt':
      return <FileText size={24} />;
    default:
      return <FileIcon size={24} />;
  }
};

export default FileUpload;